import { Box, Checkbox, Grid, Typography, useMediaQuery } from "@mui/material";
import React from "react";
import {
  bgColor07,
  bgColor15,
  blackColor,
  blackShade19,
  blueColor04,
  greyColor060,
  greyColor21,
  progressBGColor,
  whiteColor,
} from "../../../configs/styles/muiThemes";
import { textOneLines, textTwoLines } from "../../../utils/styles";
import SearchBoxInput from "../../../components/Input/SearchBoxInput";
import InventoryFilterCard from "../../Inventory/Cards/InventoryFilterCard";
import SearchInfoCard from "./SearchInfoCard";

import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import EmptyCard from "../../../components/Cards/EmptyCard";
import { toTitleCase } from "../../../utils/validationUtils";

const InventoryDetailsTable = ({
  isCatalogPartner,
  catalogPartner,
  productDetails,
  selectAllProductRows,
  toggleProductRowSelect,

  searchQuery,
  setSearchQuery,
  handleSearch,
  applyONDCFilter,
  isClearFilter,
  setIsClearFilter,
  filterType,
  setFilterType,
  isClearSearch,
  setIsClearSearch,
  clearSearchFilter,
  ondcDropdownData,
  selectedOndcCategory,
  setSelectedOndcCategory,
  selectedOndcSubCat,
  setSelectedOndcSubCat,
  refreshProductList,
}) => {
  const matches = useMediaQuery((theme) => theme.breakpoints.up("md"));

  const TABLE_HEADER = [
    { id: "01", title: "", col: 0.3 },
    {
      id: "02",
      title: "Product Info",
      subText: `(Total ${productDetails?.length} Products)`,
      col: 5.3,
    },
    { id: "03", title: "Category & Sub Category", col: 2.5 },
    { id: "04", title: "Available Qty", col: 1.3 },
    { id: "05", title: "Original Price", col: 1.3 },
    { id: "06", title: "Sale Price", col: 1.3 },
  ];

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
      >
        <Box>
          <Typography sx={styles.heading}>
            Download Current{" "}
            {isCatalogPartner ? "Pricing" : "Inventory/Pricing"} Details
          </Typography>
          <Typography sx={styles.subText}>
            Download the latest{" "}
            {isCatalogPartner ? "pricing" : "inventory/pricing"} data, update
            details and upload seamlessly.
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={2}>
          <SearchBoxInput
            placeholder="Search Product"
            value={searchQuery}
            onChange={(text) => {
              setSearchQuery(text);
            }}
            onSearchClick={handleSearch}
            minWidth={matches ? "255px" : "calc(100vw - 30px)"}
            isDashboardInput
          />

          <Box display="flex" alignItems="flex-start">
            <InventoryFilterCard
              applyONDCFilter={applyONDCFilter}
              setIsClearFilter={setIsClearFilter}
              setFilterType={setFilterType}
              // ONDC Category
              ondcDropdownData={ondcDropdownData}
              selectedOndcCategory={selectedOndcCategory}
              setSelectedOndcCategory={setSelectedOndcCategory}
              selectedOndcSubCat={selectedOndcSubCat}
              setSelectedOndcSubCat={setSelectedOndcSubCat}
              refreshProductList={refreshProductList}
            />
          </Box>
        </Box>
      </Box>

      {(isClearSearch || isClearFilter) && (
        <SearchInfoCard
          searchQuery={searchQuery}
          clearSearch={() => {
            clearSearchFilter();
          }}
          filterType={filterType}
          isClearFilter={isClearFilter}
          data={productDetails}
        />
      )}

      {productDetails?.length == 0 && (
        <EmptyCard
          msg={
            isClearSearch
              ? "No matching products found. Please refine your search"
              : "No active products are available for Inventory/Pricing Update"
          }
          icon={<ContentPasteIcon sx={{ fontSize: "24px" }} />}
          noMargin={!matches}
        />
      )}

      {productDetails?.length > 0 && (
        <Box
          sx={{
            border: `1px solid ${progressBGColor}`,
            borderRadius: "10px",
            mt: 1,
          }}
        >
          <Box borderBottom={`1px solid ${progressBGColor}`} p="6px 10px">
            <Grid container spacing={2}>
              {TABLE_HEADER.map((item, index) => (
                <Grid key={item.id} item md={item.col}>
                  {index === 0 ? (
                    <Checkbox
                      // size=""
                      checked={productDetails?.every((item) => item.isSelected)}
                      onChange={({ target }) => {
                        selectAllProductRows(target.checked);
                      }}
                      sx={styles.checkbox}
                    />
                  ) : (
                    <>
                      <Typography
                        fontSize={12}
                        fontWeight={400}
                        color={blackColor}
                      >
                        {item.title}

                        {item?.subText && (
                          <Typography
                            component="span"
                            fontSize={10.5}
                            fontWeight={600}
                            color={blueColor04}
                            ml={1}
                          >
                            {item.subText}
                          </Typography>
                        )}
                      </Typography>
                    </>
                  )}
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box sx={styles.listContainer}>
            {productDetails?.map((item, index) => (
              <Box
                key={`sku_${index}`}
                bgcolor={index % 2 == 0 ? whiteColor : bgColor07}
                p="10px"
              >
                <Grid container spacing={2}>
                  <Grid item md={0.3}>
                    <Checkbox
                      // size=""
                      checked={item?.isSelected || false}
                      onChange={() => {
                        toggleProductRowSelect(index, item?.isSelected);
                      }}
                      sx={styles.checkbox}
                    />
                  </Grid>

                  <Grid item md={5.3}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Box sx={styles.imgBox}>
                        <Box
                          component="img"
                          src={item?.productPhoto?.docURL}
                          sx={styles.imgContainer}
                        />
                      </Box>

                      <Typography
                        flex={1}
                        fontSize={12}
                        fontWeight={500}
                        color={blackColor}
                        lineHeight="20px"
                        title={item?.productName}
                        sx={{ ...textTwoLines }}
                      >
                        {item?.productName}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item md={2.5}>
                    <Typography
                      fontSize={12}
                      fontWeight={600}
                      color={blackColor}
                      lineHeight="18px"
                      sx={{ ...textOneLines }}
                    >
                      {item?.productMainCategory?.[0]?.value}
                    </Typography>
                    <Typography
                      fontSize={11}
                      fontWeight={500}
                      color={bgColor15}
                      sx={{ ...textOneLines }}
                    >
                      {item?.productSubCategory?.[0]?.value}
                    </Typography>
                  </Grid>

                  <Grid item md={1.3}>
                    <Typography
                      fontSize={12}
                      fontWeight={500}
                      color={blackColor}
                      sx={{ ...textOneLines }}
                    >
                      {isCatalogPartner
                        ? `Refer ${toTitleCase(catalogPartner)}`
                        : item?.availableQuantity}
                    </Typography>
                  </Grid>

                  <Grid item md={1.3}>
                    <Typography
                      fontSize={12}
                      fontWeight={600}
                      color={blackColor}
                      sx={{ ...textOneLines }}
                    >
                      ₹{item?.productOriginalPrice}
                    </Typography>
                  </Grid>

                  <Grid item md={1.3}>
                    <Typography
                      fontSize={12}
                      fontWeight={600}
                      color={blackColor}
                      sx={{ ...textOneLines }}
                    >
                      ₹{item?.productSalePrice}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default InventoryDetailsTable;

const styles = {
  heading: {
    fontSize: "14px",
    fontWeight: "600",
    color: blackShade19,
  },

  subText: {
    fontSize: "12px",
    fontWeight: "500",
    color: "rgb(118, 127, 145);",
    mb: "0px",
  },

  listContainer: {
    height: "42vh",
    overflowY: "scroll",
    "&::-webkit-scrollbar": {
      width: "2px",
      height: "2px",
      borderRadius: "50px !important",
    },
    "&::-webkit-scrollbar-thumb": {
      // background: '#d0d3da',
      background: blueColor04,
    },
    "&::-webkit-scrollbar-track": {
      background: "rgba(0,0,0,0.05)",
    },
  },

  imgBox: {
    width: { xs: "25px", md: "45px !important" },
    height: { xs: "25px", md: "45px !important" },
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    border: "1px solid #e5e9f0",
    // p: "5px",
    cursor: "pointer",
  },

  imgContainer: {
    backgroundColor: whiteColor,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: { xs: "25px", md: "38px !important" },
    height: { xs: "25px", md: "38px !important" },
    objectFit: "contain",
  },

  checkbox: {
    color: greyColor21,
    p: 0,
    mt: -0.5,
    transform: "scale(1.3)",
    "&.Mui-checked": {
      color: blueColor04,
    },
  },
};
